import * as R from 'ramda';
import { createSelector } from 'reselect';
import { searchResultsColumnSettings } from './settings/result-settings';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// features
import * as H from './helpers';
//////////////////////////////////////////////////

const store = (state) => state.loadBoard;

const makeSelectDataLoading = () => createSelector(
  store,
  ({ loading }) => loading,
);

const makeSelectInitialPageDataLoaded = () => createSelector(
  store,
  ({ initialPageDataLoaded }) => initialPageDataLoaded,
);

const makeSelectAuthorizedLoadBoards = () => createSelector(
  store,
  ({ authorizedLoadBoards }) => authorizedLoadBoards,
);

const makeSelectShipmentList = () => createSelector(
  store,
  ({
    shipments,
    cancelledShown,
    titleSortValues,
    tableTitleFilters,
    filteredBySearchFilter,
  }) => {
    let filtered = shipments;

    if (R.not(cancelledShown)) {
      filtered = R.filter(
        ({ cancelled }) => R.not(cancelled),
        shipments,
      );
    }

    if (G.isNotEmpty(filteredBySearchFilter)) {
      filtered = R.filter(
        ({ searchFilterGuid }) => R.includes(searchFilterGuid, filteredBySearchFilter),
        filtered,
      );
    }

    filtered = H.getFilteredShipments(
      tableTitleFilters,
      R.take(
        GC.SHIPMENT_LIMIT,
        filtered,
      ),
    );

    return H.getSortedShipments(titleSortValues, filtered);
  },
);

// TODO: check next in post tel
const makeSelectRequiredMinMaxPrice = () => createSelector(
  store,
  ({ requiredMinMaxPrice }) => requiredMinMaxPrice,
);

const makeSelectSearchFilters = () => createSelector(
  store,
  ({ searchFilters }) => R.values(searchFilters),
);

const makeSelectCompanyFilterList = () => createSelector(
  store,
  ({ companyFilters }) => companyFilters,
);

const makeSelectUsedReport = () => createSelector(
  store,
  ({ usedReport }) => {
    const report = R.clone(usedReport);

    const { fields } = report;

    const validFields = R.filter(
      R.compose((name) => searchResultsColumnSettings[name], R.prop('name')),
      fields,
    );

    report.fields = validFields;

    return report;
  },
);

const makeSelectAvailableReports = () => createSelector(
  store,
  ({ availableReports }) => availableReports,
);

const makeSelectPinnedReportsArePresent = () => createSelector(
  store,
  ({ availableReports }) => R.any(R.propEq(true, 'pinnedReport'), availableReports),
);

const makeSelectCancelledShown = () => createSelector(
  store,
  ({ cancelledShown }) => cancelledShown,
);

const makeSelectAutodialConfigs = () => createSelector(
  store,
  ({ autodialOn, autodialAppName, autodialActionOn }) => ({ autodialOn, autodialAppName, autodialActionOn }),
);

const makeSelectIndexedSearchFilterList = () => createSelector(
  store,
  ({ searchFilters }) => searchFilters,
);

const makeSelectSearchResultsByFilterId = () => createSelector(
  store,
  ({ searchResultsByFilterId }) => searchResultsByFilterId,
);

const excludeTruckerTools = (config) => R.filter(
  G.notPropEq(GC.EXTERNAL_LOAD_BOARD_TRUCKER_TOOLS, GC.LOGIN_EXTERNAL_LOAD_BOARD_TYPE),
  config,
);

const makeSelectLoadBoardConfigList = createSelector(
  R.path(['config', 'integration']),
  R.compose(
    R.sortBy(R.prop('index')),
    R.values,
    R.prop('loadBoardConfigList'),
  ),
);

const makeSelectAvailableLoadBoards = () => createSelector(
  [store, makeSelectLoadBoardConfigList],
  ({ authorizedLoadBoards = [] }, loadBoardConfigs) => R.filter(
    R.both(
      R.propEq(GC.LB_STATUS_ACTIVE, GC.FIELD_STATUS),
      R.compose(
        R.flip(R.includes)(GC.EXTERNAL_LOAD_BOARDS_TO_SEARCH),
        R.prop(GC.LOGIN_EXTERNAL_LOAD_BOARD_TYPE),
      ),
    ),
    R.concat(excludeTruckerTools(loadBoardConfigs), authorizedLoadBoards),
  ),
);

// TODO:
// Check carrier authorized load boards select and source
// Check post tel available load boards and source
const makeSelectPostLoadBoards = () => createSelector(
  [store, makeSelectLoadBoardConfigList],
  ({ authorizedLoadBoards }, loadBoardConfigs) => R.filter(
    R.both(
      R.propEq(GC.LB_STATUS_ACTIVE, GC.FIELD_STATUS),
      R.compose(
        R.flip(R.includes)(GC.EXTERNAL_LOAD_BOARDS_TO_POST),
        R.prop(GC.LOGIN_EXTERNAL_LOAD_BOARD_TYPE),
      ),
    ),
    R.concat(loadBoardConfigs, authorizedLoadBoards),
  ),
);

const makeSelectTemplates = () => createSelector(
  store,
  ({ templates }) => R.values(templates),
);

const makeSelectSavedShipments = () => createSelector(
  store,
  ({ savedShipments }) => savedShipments,
);

const makeSelectBookedShipments = () => createSelector(
  store,
  ({ bookedShipments }) => bookedShipments,
);

const makeSelectCallAlarm = () => createSelector(
  store,
  ({ callAlarm }) => callAlarm,
);

const makeSelectNotifications = () => createSelector(
  store,
  ({ shipments }) => R.filter(R.propEq(true, 'highlighted'), shipments),
);

const makeSelectSearchFilterStates = () => createSelector(
  store,
  ({ searchFiltersStates }) => searchFiltersStates,
);

const makeSelectTitleSortValues = () => createSelector(
  store,
  ({ titleSortValues }) => titleSortValues,
);

const makeSelectTableTitleFilters = () => createSelector(
  store,
  ({ tableTitleFilters }) => tableTitleFilters,
);

const makeSelectFilteredBySearchFilter = () => createSelector(
  store,
  ({ filteredBySearchFilter }) => filteredBySearchFilter,
);

const makeSelectSearchFilterColors = () => createSelector(
  store,
  ({ searchFilterColors }) => searchFilterColors,
);

const makeSelectSearchFiltersCalled = () => createSelector(
  store,
  ({ searchFiltersCalled }) => searchFiltersCalled,
);

export {
  makeSelectCallAlarm,
  makeSelectTemplates,
  makeSelectUsedReport,
  makeSelectDataLoading,
  makeSelectShipmentList,
  makeSelectNotifications,
  makeSelectSearchFilters,
  makeSelectCancelledShown,
  makeSelectPostLoadBoards,
  makeSelectSavedShipments,
  makeSelectBookedShipments,
  makeSelectAutodialConfigs,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectCompanyFilterList,
  makeSelectTableTitleFilters,
  makeSelectSearchFilterStates,
  makeSelectSearchFilterColors,
  makeSelectAvailableLoadBoards,
  makeSelectRequiredMinMaxPrice,
  makeSelectSearchFiltersCalled,
  makeSelectAuthorizedLoadBoards,
  makeSelectInitialPageDataLoaded,
  makeSelectFilteredBySearchFilter,
  makeSelectIndexedSearchFilterList,
  makeSelectPinnedReportsArePresent,
  makeSelectSearchResultsByFilterId,
};
